import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';

interface TutorialStep {
  id: number;
  title: string;
  description: string;
  target: string; // CSS selector for the element to highlight
  position: 'top' | 'bottom' | 'left' | 'right';
  content: string;
}

interface TutorialOverlayProps {
  isVisible: boolean;
  onComplete: () => void;
  onSkip: () => void;
  currentStep: number;
  onStepChange: (step: number) => void;
}

const tutorialSteps: TutorialStep[] = [
  {
    id: 1,
    title: "Overview",
    description: "Your main dashboard",
    target: ".overview-section",
    position: "top",
    content: "See a summary of client performance, engagement, and quick actions at a glance."
  },
  {
    id: 2,
    title: "Clients",
    description: "Manage your clients",
    target: ".clients-section",
    position: "top",
    content: "Browse, search, and select clients to view details and take actions."
  },
  {
    id: 3,
    title: "Meals",
    description: "Monitor client nutrition",
    target: ".meals-section",
    position: "top",
    content: "Review meal logs and nutrition data to keep clients on track."
  },
  {
    id: 4,
    title: "Insights",
    description: "Analytics & progress",
    target: ".insights-section",
    position: "top",
    content: "Dive into analytics, trends, and performance insights for each client."
  },
  {
    id: 5,
    title: "Diet (Goal Setting)",
    description: "Set and track goals",
    target: ".diet-section",
    position: "top",
    content: "Calculate and set personalized diet goals tailored to each client."
  },
  {
    id: 6,
    title: "Trends",
    description: "Long‑term progress",
    target: ".trends-section",
    position: "top",
    content: "Visualize progress over time to spot patterns and celebrate wins."
  },
  {
    id: 7,
    title: "Leaderboard",
    description: "Friendly competition",
    target: ".leaderboard-section",
    position: "top",
    content: "Motivate clients with ranks and achievements among their peers."
  },
  {
    id: 8,
    title: "Pricing",
    description: "Manage your plan",
    target: ".subscription-section",
    position: "top",
    content: "View and manage subscription plans and billing options."
  }
];

export const TutorialOverlay: React.FC<TutorialOverlayProps> = ({
  isVisible,
  onComplete,
  onSkip,
  currentStep,
  onStepChange
}) => {
  const [highlightedElement, setHighlightedElement] = useState<HTMLElement | null>(null);
  const [overlayPosition, setOverlayPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });

  const currentTutorialStep = tutorialSteps.find(step => step.id === currentStep);

  useEffect(() => {
    if (!isVisible || !currentTutorialStep) return;

    // Find the target element
    const targetElement = document.querySelector(currentTutorialStep.target) as HTMLElement;
    
    if (targetElement) {
      setHighlightedElement(targetElement);
      
      // Calculate position for the highlight overlay with padding
      const rect = targetElement.getBoundingClientRect();
      const padding = 8; // visual padding around the target
      const top = Math.max(0, rect.top - padding);
      const left = Math.max(0, rect.left - padding);
      const width = Math.min(window.innerWidth - left, rect.width + padding * 2);
      const height = Math.min(window.innerHeight - top, rect.height + padding * 2);
      
      setOverlayPosition({
        top,
        left,
        width,
        height
      });

      // Scroll element into view if needed
      targetElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'center'
      });
    }
  }, [isVisible, currentStep, currentTutorialStep]);

  const handleNext = async () => {
    if (currentStep < tutorialSteps.length) {
      onStepChange(currentStep + 1);
    } else {
      await completeTutorial();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      onStepChange(currentStep - 1);
    }
  };

  const handleSkip = async () => {
    await skipTutorial();
    onSkip();
  };

  const completeTutorial = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.rpc('complete_tutorial', { trainer_uuid: user.id });
      }
      onComplete();
    } catch (error) {
      console.error('Error completing tutorial:', error);
      onComplete(); // Still complete locally even if DB fails
    }
  };

  const skipTutorial = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.rpc('skip_tutorial', { trainer_uuid: user.id });
      }
    } catch (error) {
      console.error('Error skipping tutorial:', error);
    }
  };

  if (!isVisible || !currentTutorialStep) return null;

  return (
    <>
      {/* Dark overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" />
      
      {/* Highlight overlay */}
      {highlightedElement && (
        <div
          className="fixed z-50 border-2 border-blue-500 bg-blue-500 bg-opacity-20 rounded-lg transition-all duration-300"
          style={{
            top: overlayPosition.top - 4,
            left: overlayPosition.left - 4,
            width: overlayPosition.width + 8,
            height: overlayPosition.height + 8,
          }}
        />
      )}

      {/* Tutorial content (always centered) */}
      <div className="fixed inset-0 z-50 pointer-events-none">
        <div
          className="pointer-events-auto bg-white rounded-lg shadow-xl p-6 max-w-md w-[90vw] md:w-auto absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900">{currentTutorialStep.title}</h3>
              <p className="text-sm text-gray-600">{currentTutorialStep.description}</p>
            </div>
            <button
              onClick={handleSkip}
              className="text-gray-400 hover:text-gray-600 text-sm font-medium"
            >
              Skip
            </button>
          </div>

          {/* Content */}
          <p className="text-gray-700 mb-6">{currentTutorialStep.content}</p>

          {/* Progress indicator */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex space-x-1">
              {tutorialSteps.map((step) => (
                <div
                  key={step.id}
                  className={`w-2 h-2 rounded-full ${
                    step.id <= currentStep ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
            <span className="text-sm text-gray-500">
              {currentStep} of {tutorialSteps.length}
            </span>
          </div>

          {/* Navigation buttons */}
          <div className="flex justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Previous
            </button>
            
            <button
              onClick={handleNext}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
            >
              {currentStep === tutorialSteps.length ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
